import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';

import { studentBillingApi } from '@/api';
import { Alert, Button, Input, Modal } from '@/components/ui';
import { formatMoney, formatPrice } from '@/features/billing/money';
import { CHANGE, planChangeCopy, startsNow } from '@/features/billing/planChange';
import { errorMessage } from '@/hooks/useApiError';

/**
 * Confirm a plan change: an upgrade (or a switch to another currency) starts
 * now and is invoiced now; a downgrade waits for the end of the paid period.
 *
 * A discount code applies only to a change that issues an invoice, so the
 * "Have a coupon?" field appears for upgrades and switches. The code is priced
 * first by the preview endpoint, which writes nothing; only a code that
 * previewed is sent -- the server checks it again either way.
 *
 * Render with `key` set to the plan's code so a reopened dialog never carries
 * a discount priced for a different plan.
 */
export function StudentUpgradeDialog({
  plan,
  kind = CHANGE.UPGRADE,
  currentName,
  periodEnd,
  isLoading,
  onCancel,
  onConfirm,
}) {
  const [showCode, setShowCode] = useState(false);
  const [code, setCode] = useState('');
  const [applied, setApplied] = useState(null);
  const billsNow = startsNow(kind);
  // A code is offered for any paid plan. With a scheduled change it is checked
  // now and kept for the new plan's first bill.
  const takesCode = (plan.price_cents ?? 0) > 0;
  const copy = planChangeCopy(kind, { planName: plan.name, currentName, periodEnd });

  const preview = useMutation({
    mutationFn: () => studentBillingApi.previewCoupon(code.trim(), plan.code),
    onSuccess: (result) => setApplied(result),
    onError: () => setApplied(null),
  });

  const total = applied ? applied.total_cents : plan.price_cents;

  return (
    <Modal
      open
      title={copy.title}
      description={
        billsNow
          ? `${copy.description} Price: ${formatPrice(plan.price_cents, plan.currency, plan.interval)}.`
          : copy.description
      }
      onClose={onCancel}
      footer={
        <>
          <Button variant="secondary" onClick={onCancel} disabled={isLoading}>
            Cancel
          </Button>
          <Button
            isLoading={isLoading}
            onClick={() => onConfirm(plan.code, applied ? applied.code : undefined)}
          >
            {billsNow && total === 0 ? `Start ${plan.name}` : copy.confirm}
          </Button>
        </>
      }
    >
      {takesCode && (
        <div className="space-y-4">
          {showCode ? (
            <form
              className="flex items-end gap-2"
              onSubmit={(event) => {
                event.preventDefault();
                if (code.trim().length >= 3) preview.mutate();
              }}
            >
              <Input
                label="Coupon code"
                autoFocus
                autoComplete="off"
                wrapperClassName="flex-1"
                className="font-mono uppercase"
                value={code}
                onChange={(event) => {
                  setCode(event.target.value);
                  // The old price no longer describes the code in the box.
                  setApplied(null);
                  preview.reset();
                }}
              />
              <Button
                type="submit"
                variant="secondary"
                disabled={code.trim().length < 3}
                isLoading={preview.isPending}
              >
                Apply
              </Button>
            </form>
          ) : (
            <button
              type="button"
              onClick={() => setShowCode(true)}
              className="text-brand-700 hover:text-brand-800 text-sm font-medium underline-offset-2 hover:underline"
            >
              Have a coupon?
            </button>
          )}

          {preview.isError && (
            <Alert tone="warning">{errorMessage(preview.error, 'That code is not valid')}</Alert>
          )}

          {applied && (
            <Alert tone="success" title={`${applied.code} applied`}>
              {applied.description} {billsNow ? 'Your invoice' : `${plan.name}'s first bill`} will
              be <strong>{formatMoney(applied.total_cents, applied.currency)}</strong> instead of{' '}
              {formatMoney(applied.plan_price_cents, applied.currency)}
              {applied.recurring ? ', and every renewal is discounted too.' : '.'}
              {!billsNow && ' Nothing is billed today.'}
            </Alert>
          )}
        </div>
      )}
    </Modal>
  );
}
